<template>
    <div>
        <div id="editor" v-bind:style="currentStyle" ref="editor">
            <div v-if="fatalError">
                <p class="alert alert-danger">
                    Es ist etwas schiefgegangen bitte lade die Seite nochmal neu!
                </p>
            </div>
            <div class="editor-loading-overlay" :class="{'hidden': this.editorLoaded}">
                <p class="text-center loading">
                    <span class="loading-spinner loading-spinner-big loading-spinner-green"></span>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import * as monaco from 'monaco-editor-core'
import {listen} from 'vscode-ws-jsonrpc';
import ReconnectingWebSocket from 'reconnecting-websocket';
import {
    MonacoLanguageClient,
    CloseAction,
    ErrorAction,
    MonacoServices,
    createConnection
} from 'monaco-languageclient';

const normalizeUrl = require('normalize-url');
self.MonacoEnvironment = {
    getWorkerUrl: function (moduleId, label) {
        if (label === "json") {
            return '/json.worker.bundle.' + process.env.VERSION + '.js';
        }
        return '/editor.worker.bundle.' + process.env.VERSION + '.js';
    }
};
// supported programming-languages
import 'monaco-languages/release/esm/javascript/javascript.contribution'
import 'monaco-languages/release/esm/java/java.contribution'
import 'monaco-languages/release/esm/python/python.contribution'
import 'monaco-languages/release/esm/php/php.contribution'
import 'monaco-languages/release/esm/csharp/csharp.contribution'
import 'monaco-languages/release/esm/cpp/cpp.contribution'
import 'monaco-languages/release/esm/dockerfile/dockerfile.contribution'
import '../../languages/logo/logo.contribution'
import {eventBus} from "../../main";
import {SIGNS} from "../../const";
import {HandleDiagnosticsSignature} from "vscode-languageclient/lib/client";

export default {
    name: "EditorX",
    mounted() {
        this.initializeEditor();
        if (!this.readOnly && this.workspaceUrl !== null) {
            this.initializeLanguageServerCommunication();
        }
        eventBus.$emit("editorReady");

        // when resizing the window the editor should also be resized
        this.resizeObserver = new ResizeObserver(() => {this.calculateStyle(); window.dispatchEvent(new Event('resize')); }).observe(this.$refs.editor);
        this.$nextTick(function () {
            window.addEventListener('resize', this.calculateStyle);
        });

        this.calculateStyle();

        this.editor.onDidPaste((e) => {
            let model = this.model;
            let partOfTheText = model.getValueInRange(e);
            let editorContent = model.getValue();
            let detected = false;

            for (const sign of SIGNS) {
                let regExp = new RegExp(sign.sign, 'gi');
                if (regExp.test(partOfTheText)) {
                    editorContent = editorContent.replace(regExp, sign.replacement);
                    detected = true;
                }
            }

            if (detected) {
                this.model.setValue(editorContent);
                this.editor.setPosition({column: e.endColumn, lineNumber: e.endLineNumber});

                eventBus.$emit("runnTests", {code: partOfTheText});
            }

        })

        eventBus.$on("toggleTheme", (theme) => {
            if (theme == 'dark') {
                monaco.editor.setTheme("vs-dark");
            } else {
                monaco.editor.setTheme('entwicklerheld');
            }
        });
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this.$refs.editor);
        }
    },
    destroyed() {
        this.model.dispose();
        this.editor.dispose();
        if (!this.readOnly && this.workspaceUrl !== null) {
            this.webSocket.close();
        }
        delete this.resizeObserver;
    },
    data() {
        return {
            rootUri: "file:///data/task-data",
            model: null,
            editor: null,
            webSocket: null,
            fatalError: false,
            editorLoaded: false,
            currentStyle: null,
            languageServerDiagnosticsInitialized: false,
            resizeObserver: {type: Object, default: null},
        }
    },
    props: {
        workspaceUrl: {type: String, default: null},
        programmingLanguage: {type: String,},
        code: {type: String},
        readOnly: {type: Boolean, default: false},
        minimap: {type: Boolean, default: true},
        filePath: {type: String, default: ''},
        theme: {type: String, default: 'vs-dark'},
        width: {type: [String, Number], default: '100%'},
        height: {type: [String, Number], default: undefined},
        additionalOptions: {
            type: Object, default: () => {
                return {
                    renderIndentGuides: true,
                }
            }
        }
    },
    methods: {
        initializeEditor: function () {
            this.model = monaco.editor.createModel(this.code, this.programmingLanguage, monaco.Uri.parse(this.rootUri + "/" + this.filePath));
            let options = Object.assign({
                model: this.model,
                glyphMargin: true,
                language: this.programmingLanguage,
                lightbulb: {
                    enabled: true
                },
                readOnly: this.readOnly,
                minimap: {
                    enabled: this.minimap,
                    renderCharacters: false
                },
                automaticLayout: true,
                wordBasedSuggestions: false,
                fontFamily: "Source Code Pro, Courier New, Courier, monospace",
            }, this.additionalOptions);
            this.editor = monaco.editor.create(this.$refs.editor, options);

            // Check if Language Service is already installed in Monaco Service
            try {
                MonacoServices.install(this.editor, {rootUri: this.rootUri});
            } catch (error) {
                //overwrite exception is okay
            }
            this.editor.layout();
            this.setEditorTheme(this.editor);
            this.model.onDidChangeContent((event) => {
                eventBus.$emit("editorChangedContent", {fileContent: this.model.getValue()});
            });
            // this is a current workaround for an ready event of the monacho editor
            // (https://github.com/Microsoft/monaco-editor/issues/115)
            let didScrollChangeDisposable = this.editor.onDidScrollChange(function () {
                didScrollChangeDisposable.dispose();
                this.editorLoaded = true;
            }.bind(this));
        },
        createWsUrl: function (path) {
            const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
            const workspaceUrlWithOutProtocol = this.workspaceUrl.replace("http://", "").replace("https://", "");

            return normalizeUrl(`${protocol}://${workspaceUrlWithOutProtocol}/${path}`);
        },
        createWebSocket: function (url) {
            const socketOptions = {
                maxReconnectionDelay: 10000,
                minReconnectionDelay: 1000,
                reconnectionDelayGrowFactor: 1.3,
                connectionTimeout: 10000,
                // TODO: check if this causes trouble because websocket connection is closed and ls support don't work
                // We have to do this for disabling multiple ls registrations because socket is reopened automatically
                maxRetries: 10,
                debug: false
            };
            return new ReconnectingWebSocket(url, undefined, socketOptions);
        },
        createLanguageClient: function (connection) {
            return new MonacoLanguageClient({
                name: "Sample Language Client",
                clientOptions: {
                    // use a language id as a document selector
                    documentSelector: [this.programmingLanguage],
                    // disable the default error handler
                    errorHandler: {
                        error: () => ErrorAction.Continue,
                        closed: () => CloseAction.DoNotRestart
                    },
                    middleware: {
                        handleDiagnostics: (uri, diagnostics, next) => {
                            // fix initial red lines of c# language server
                            if (this.programmingLanguage === 'csharp' && !this.languageServerDiagnosticsInitialized) {
                                let editorContent = this.model.getValue();
                                let cursorPosition = this.editor.getPosition();
                                this.model.setValue(editorContent);
                                this.editor.setPosition(cursorPosition);
                                this.languageServerDiagnosticsInitialized = true;
                            }
                            next(uri, diagnostics);
                        }
                    }
                },
                // create a language client connection from the JSON RPC connection on demand
                connectionProvider: {
                    get: (errorHandler, closeHandler) => {
                        return Promise.resolve(createConnection(connection, errorHandler, closeHandler))
                    }
                }
            });
        },
        initializeLanguageServerCommunication: function () {
            const url = this.createWsUrl('/ls');
            const webSocket = this.createWebSocket(url);
            this.webSocket = webSocket;
            listen({
                webSocket,
                onConnection: connection => {
                    // create and start the language client
                    const languageClient = this.createLanguageClient(connection);
                    const disposable = languageClient.start();
                    connection.onClose(() => {
                        disposable.dispose();
                    });

                }
            });
        },
        setEditorTheme: function (editor) {
            //TODO We need to store this externally
            monaco.editor.defineTheme('entwicklerheld', {
                base: 'vs',
                inherit: true,
                rules: [
                    {
                        background: 'e2e9ff',
                        token: 'text.html source.active4d'
                    },
                    {
                        foreground: '000000',
                        token: 'text.xml'
                    },
                    {
                        foreground: 'af82d4',
                        token: 'comment.line'
                    },
                    {
                        foreground: 'af82d4',
                        token: 'comment.block'
                    },
                    {
                        foreground: '329998',
                        token: 'string'
                    },
                    {
                        foreground: '66ccff',
                        fontStyle: 'bold',
                        token: 'string.interpolated variable'
                    },
                    {
                        foreground: 'a8017e',
                        token: 'constant.numeric'
                    },
                    {
                        foreground: '66ccff',
                        fontStyle: 'bold',
                        token: 'constant.other.date'
                    },
                    {
                        foreground: '66ccff',
                        fontStyle: 'bold',
                        token: 'constant.other.time'
                    },
                    {
                        foreground: 'a535ae',
                        token: 'constant.language'
                    },
                    {
                        foreground: '6392ff',
                        fontStyle: 'bold',
                        token: 'variable.other.local'
                    },
                    {
                        foreground: '0053ff',
                        fontStyle: 'bold',
                        token: 'variable'
                    },
                    {
                        foreground: '6988ae',
                        token: 'variable.other.table-field'
                    },
                    {
                        foreground: '5463e5',
                        fontStyle: 'bold',
                        token: 'keyword'
                    },
                    {
                        foreground: 'ff5600',
                        token: 'storage'
                    },
                    {
                        foreground: '21439c',
                        token: 'entity.name.type'
                    },
                    {
                        foreground: '21439c',
                        token: 'entity.name.function'
                    },
                    {
                        foreground: '7a7a7a',
                        token: 'meta.tag'
                    },
                    {
                        foreground: '016cff',
                        token: 'entity.name.tag'
                    },
                    {
                        foreground: '963dff',
                        token: 'entity.other.attribute-name'
                    },
                    {
                        foreground: '45ae34',
                        fontStyle: 'bold',
                        token: 'support.function'
                    },
                    {
                        foreground: 'b7734c',
                        token: 'support.constant'
                    },
                    {
                        foreground: 'a535ae',
                        token: 'support.type'
                    },
                    {
                        foreground: 'a535ae',
                        token: 'support.class'
                    },
                    {
                        foreground: 'a535ae',
                        token: 'support.variable'
                    },
                    {
                        foreground: 'ffffff',
                        background: '990000',
                        token: 'invalid'
                    },
                    {
                        foreground: 'ffffff',
                        background: '656565',
                        token: 'meta.diff'
                    },
                    {
                        foreground: 'ffffff',
                        background: '1b63ff',
                        token: 'meta.diff.range'
                    },
                    {
                        foreground: '000000',
                        background: 'ff7880',
                        token: 'markup.deleted.diff'
                    },
                    {
                        foreground: '000000',
                        background: '98ff9a',
                        token: 'markup.inserted.diff'
                    },
                    {
                        foreground: '5e5e5e',
                        token: 'source.diff'
                    }
                ],
            });
            if (document.documentElement.hasAttribute('theme') && document.documentElement.getAttribute('theme') == 'dark') {
                monaco.editor.setTheme('vs-dark');
            } else {
                monaco.editor.setTheme('entwicklerheld');
            }
        },
        convertRemToPixels(rem) {
            return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
        },
        calculateStyle() {
            const {width, height} = this;
            const fixedWidth = width.toString().indexOf('%') !== -1 ? width : `${width}px`;

            if (height && height != 0) {
                const fixedHeight = width.toString().indexOf('%') !== -1 ? height : `${height}px`;
                this.currentStyle = {
                    width: fixedWidth,
                    height: fixedHeight,
                    margin: "6px auto",
                    "box-sizing": "border-box"
                };
                return
            }

            // Get the window height browser independently
            let windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
            // remove the header bar

            let navbarHeight = 62;
            try {
                navbarHeight = document.getElementById('navbar_row').offsetHeight;
            } catch (err) {
                console.warn(err);
            }
            let heightOfConsoleOutput = this.convertRemToPixels(4);
            windowHeight = windowHeight - navbarHeight - 13 - heightOfConsoleOutput;

            const fixedHeight = `${windowHeight}px`;
            this.currentStyle = {
                width: fixedWidth,
                height: fixedHeight,
                margin: "6px auto",
                "box-sizing": "border-box"
            };
        },
    }
}
</script>


<style scoped>
#editor {
    width: 800px;
    height: 600px;
}

.loading {
    position: absolute;
    top: 45%;
    width: 100%;
}

.loading-spinner {
    color: #08AB99;
    font-family: FontAwesome !important;
}

.editor-loading-overlay {
    background-color: white;
    z-index: 90;
    position: absolute;
    height: 100%;
    width: 100%;
    transition: 0.2s;
}

.hidden {
    display: none;
}
</style>
